/**
 * O que o checkout (4.1) e a pagina de cobranca (4.3, mensalidade e portao do avanco) tem em comum: falar com o servico de
 * pagamentos, PIX e cartao. O cartao e tokenizado direto na Pagar.me (o numero nunca passa pelos nossos servidores).
 */
import type { MeioDoCheckout, OpcaoDeParcela, PagamentoNoCartao } from "@mtc/contratos";
import { Aviso, Botao, Campo, CampoSelecao, ErroApi, Grade, IconeCartao, IconePix, Pilha, Segmentado, formatarReais } from "@mtc/ui";
import { useState, type FormEvent } from "react";
import { capturarNoNavegador } from "@mtc/observabilidade/navegador";
import { config, marca } from "../config";

/** Resposta do servico: dados, ou o erro como veio, separando "nao existe" de "fora do ar" (decisao 13). */
export type Resultado<T> = { ok: true; dados: T } | { ok: false; status: number; erro: string; inexistente: boolean; fora: boolean; mudouPara?: string; codigo?: string };

/**
 * Codigo da ultima falha falando com o servico (o `x-codigo` da resposta, ou o do registro da falha de rede): a tela de
 * instabilidade mostra, e o suporte acha o que aconteceu.
 */
let ultimaFalha: string | null = null;

export async function chamar<T>(caminho: string, init?: RequestInit): Promise<Resultado<T>> {
  let resp: Response;
  try {
    // POST sempre com corpo JSON ("{}" quando nao ha): o servidor recusa JSON vazio.
    const corpo = init?.method === "POST" && init.body === undefined ? "{}" : init?.body;
    resp = await fetch(`${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`, { ...init, body: corpo, headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) } });
  } catch (err) {
    // So o navegador ve essa falha (rede, DNS, bloqueio): e o unico lugar pra registrar.
    ultimaFalha = capturarNoNavegador(err, { servico: "pagamentos", caminho: caminho.replace(/\/tentativas\/[^/]+/, "/tentativas/:sessao") });
    return { ok: false, status: 0, erro: "", inexistente: false, fora: true };
  }
  const corpo = (await resp.json().catch(() => null)) as (T & { erro?: string; codigo?: string; message?: string; mudou_para?: string }) | null;
  if (resp.ok && corpo) return { ok: true, dados: corpo };
  if (resp.status >= 500) ultimaFalha = resp.headers.get("x-codigo");
  const texto = corpo?.erro ?? corpo?.message ?? "";
  const erro = texto && resp.status >= 500 && ultimaFalha ? `${texto} Código do erro: ${ultimaFalha}.` : texto;
  // "Fora do ar" e quando nao veio a nossa resposta: rede, 5xx do proxy sem corpo nosso, ou erro sem mensagem.
  // Link do sistema antigo que mudou de lugar (spec 009, T12): a tela leva a pessoa pro endereco novo.
  return { ok: false, status: resp.status, erro, inexistente: corpo?.codigo === "CHECKOUT_INEXISTENTE" || corpo?.codigo === "COBRANCA_INEXISTENTE", fora: !erro || (resp.status >= 500 && !corpo?.erro), ...(corpo?.codigo === "CHECKOUT_MUDOU" && corpo.mudou_para ? { mudouPara: corpo.mudou_para } : {}) };
}

/**
 * A mesma conversa com o servico de pagamentos, mas com o cliente logado (spec 045): vai pela `api()` da sessao, que poe
 * o token da area e renova uma vez se ele venceu. Abrir a tentativa so funciona assim; sem sessao, o servico responde
 * 401 com `codigo: "PRECISA_ENTRAR"`, e a sessao assistida, 403.
 */
export async function chamarComSessao<T>(api: <R>(caminho: string, init?: RequestInit) => Promise<R>, caminho: string, init?: RequestInit): Promise<Resultado<T>> {
  try {
    const corpo = init?.method === "POST" && init.body === undefined ? "{}" : init?.body;
    return { ok: true, dados: await api<T>(`${config().PAGAMENTOS_URL.replace(/\/$/, "")}${caminho}`, { ...init, body: corpo }) };
  } catch (err) {
    if (!(err instanceof ErroApi)) {
      ultimaFalha = capturarNoNavegador(err, { servico: "pagamentos", caminho: caminho.replace(/\/tentativas\/[^/]+/, "/tentativas/:sessao") });
      return { ok: false, status: 0, erro: "", inexistente: false, fora: true };
    }
    const c = err.corpo as { erro?: string; codigo?: string; message?: string; mudou_para?: string };
    if (err.status >= 500) ultimaFalha = err.codigo ?? null;
    const texto = c?.erro ?? c?.message ?? "";
    const erro = texto && err.status >= 500 && ultimaFalha ? `${texto} Código do erro: ${ultimaFalha}.` : texto;
    // A vitrine do checkout tambem passa por aqui quando ha login (promocao de CPF, spec 067): o link antigo leva pro novo.
    return { ok: false, status: err.status, erro, inexistente: c?.codigo === "CHECKOUT_INEXISTENTE", fora: !erro || (err.status >= 500 && !c?.erro), ...(c?.codigo ? { codigo: c.codigo } : {}), ...(c?.codigo === "CHECKOUT_MUDOU" && c.mudou_para ? { mudouPara: c.mudou_para } : {}) };
  }
}

const UFS = "AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO".split(" ");
const digitos = (v: string) => v.replace(/\D/g, "");


export function Instabilidade() {
  const m = marca();
  return (
    <Aviso tipo="alerta">
      Estamos com instabilidade pra carregar o pagamento. Tente de novo em alguns minutos
      {m.whatsapp ? <> ou <a href={m.whatsapp} target="_blank" rel="noreferrer">fale com a gente no WhatsApp</a></> : null}.
      {ultimaFalha ? <> Código do erro: <strong className="mtc-mono">{ultimaFalha}</strong>.</> : null}
    </Aviso>
  );
}

/** O que se paga: o plano do checkout ou uma cobranca (a vista). Parcelas e juros vem calculados do servidor. */
export type Oferta = { meios: MeioDoCheckout[]; parcelas: OpcaoDeParcela[]; chave_publica: string | null; preco_centavos: number };

/** Passo 2: PIX ou cartao. O que o meio pede vem da vitrine (parcelas e juros calculados no servidor). */
export function Pagar({ vitrine, sessao, aviso, aoPagar }: { vitrine: Oferta; sessao: string; aviso: { tipo: "erro" | "alerta"; texto: string } | null; aoPagar: () => void }) {
  const [meio, setMeio] = useState(vitrine.meios[0] ?? "pix");
  const [erro, setErro] = useState<string | null>(null);
  const [fora, setFora] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const rodar = async (fazer: () => Promise<Resultado<unknown>>) => {
    setEnviando(true);
    setErro(null);
    setFora(false);
    const r = await fazer();
    setEnviando(false);
    if (r.ok) return aoPagar();
    if (r.fora) setFora(true);
    else setErro(r.erro);
  };
  return (
    <Pilha espaco={4}>
      <h1 className="mtc-titulo mtc-titulo--3">Pagamento</h1>
      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}
      {vitrine.meios.length > 1 && <Segmentado rotulo="Como você quer pagar" nome="meio" valor={meio} aoMudar={setMeio} opcoes={vitrine.meios.map((m) => ({ valor: m, rotulo: m === "pix" ? "PIX" : "Cartão de crédito" }))} />}
      {meio === "pix" ? (
        <Pilha espaco={3}>
          <p className="mtc-texto-2">O QR Code aparece na próxima tela. A confirmação chega em segundos.</p>
          <Botao largo icone={<IconePix tamanho={18} />} carregando={enviando} onClick={() => void rodar(() => chamar(`/v1/tentativas/${encodeURIComponent(sessao)}/pix`, { method: "POST" }))}>Gerar PIX de {formatarReais(vitrine.preco_centavos)}</Botao>
        </Pilha>
      ) : (
        <Cartao vitrine={vitrine} enviando={enviando} aoPagar={(corpo) => void rodar(() => chamar(`/v1/tentativas/${encodeURIComponent(sessao)}/cartao`, { method: "POST", body: JSON.stringify(corpo) }))} aoErro={setErro} />
      )}
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {fora && <Instabilidade />}
    </Pilha>
  );
}

/** Tokeniza direto na Pagar.me com a chave publica: numero e CVV nunca vao pro nosso servidor. */
export async function tokenizar(chavePublica: string, c: { numero: string; nome: string; validade: string; cvv: string }): Promise<{ ok: true; token: string } | { ok: false; erro: string }> {
  const [mes, ano] = c.validade.split("/");
  try {
    const r = await fetch(`https://api.pagar.me/core/v5/tokens?appId=${encodeURIComponent(chavePublica)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "card", card: { number: digitos(c.numero), holder_name: c.nome.trim(), exp_month: Number(mes), exp_year: Number(ano), cvv: digitos(c.cvv) } }),
    });
    const j = (await r.json().catch(() => ({}))) as { id?: string; message?: string; errors?: Record<string, string[]> };
    if (r.ok && j.id) return { ok: true, token: j.id };
    const detalhe = j.errors ? Object.values(j.errors).flat()[0] : null;
    return { ok: false, erro: `Não conseguimos validar o cartão${detalhe ? `: ${detalhe}` : ""}. Confira número, validade e CVV.` };
  } catch {
    return { ok: false, erro: "Não conseguimos falar com o processador do cartão. Tente de novo em instantes." };
  }
}

/** O que o comprador digita do cartao e do endereco de cobranca. */
export type DadosDoCartao = { numero: string; nome: string; validade: string; cvv: string; parcelas: string; cep: string; linha: string; cidade: string; uf: string };
export const CARTAO_VAZIO: DadosDoCartao = { numero: "", nome: "", validade: "", cvv: "", parcelas: "1", cep: "", linha: "", cidade: "", uf: "" };
export const cartaoPronto = (c: DadosDoCartao) => digitos(c.numero).length >= 13 && c.nome.trim().length >= 3 && /^\d{2}\/\d{2}$/.test(c.validade) && digitos(c.cvv).length >= 3 && digitos(c.cep).length === 8 && c.linha.trim().length >= 3 && c.cidade.trim().length >= 2 && c.uf.length === 2;
/** A opcao de parcela escolhida (vem do servidor: parcela, total e juros). */
export const parcelaEscolhida = (c: DadosDoCartao, parcelas: OpcaoDeParcela[]) => parcelas.find((p) => String(p.parcelas) === c.parcelas) ?? parcelas[0];

/** Os campos do cartao e do endereco de cobranca, controlados por quem usa (checkout e pagina de cobranca). */
export function CamposDoCartao({ valor: c, aoMudar, parcelas, formatar = formatarReais }: { valor: DadosDoCartao; aoMudar: (c: DadosDoCartao) => void; parcelas: OpcaoDeParcela[]; /** Em dolar na vitrine em dolar (15/10). */ formatar?: (centavos: number) => string }) {
  const mudar = (parte: Partial<DadosDoCartao>) => aoMudar({ ...c, ...parte });
  return (
    <>
      <Campo rotulo="Número do cartão" valor={c.numero} aoMudar={(v) => mudar({ numero: digitos(v).slice(0, 19).replace(/(\d{4})(?=\d)/g, "$1 ") })} inputMode="numeric" autoComplete="cc-number" mono required />
      <Campo rotulo="Nome impresso no cartão" valor={c.nome} aoMudar={(v) => mudar({ nome: v.toUpperCase() })} autoComplete="cc-name" required />
      <Grade minimo={120}>
        <Campo rotulo="Validade" valor={c.validade} aoMudar={(v) => mudar({ validade: digitos(v).slice(0, 4).replace(/^(\d{2})(\d)/, "$1/$2") })} inputMode="numeric" autoComplete="cc-exp" placeholder="MM/AA" mono required />
        <Campo rotulo="CVV" valor={c.cvv} aoMudar={(v) => mudar({ cvv: digitos(v).slice(0, 4) })} inputMode="numeric" autoComplete="cc-csc" mono required />
      </Grade>
      {parcelas.length > 1 && (
        <CampoSelecao busca={false}
          rotulo="Parcelas"
          valor={c.parcelas}
          aoMudar={(v) => mudar({ parcelas: v })}
          opcoes={parcelas.map((p) => ({ valor: String(p.parcelas), rotulo: p.parcelas === 1 ? `1x de ${formatar(p.total_centavos)} (à vista)` : `${p.parcelas}x de ${formatar(p.valor_parcela_centavos)}${p.juros_centavos > 0 ? ` (total ${formatar(p.total_centavos)})` : " sem juros"}` }))}
        />
      )}
      <span className="mtc-rotulo">Endereço de cobrança do cartão</span>
      <Grade minimo={120}>
        <Campo rotulo="CEP" valor={c.cep} aoMudar={(v) => mudar({ cep: digitos(v).slice(0, 8).replace(/^(\d{5})(\d)/, "$1-$2") })} inputMode="numeric" autoComplete="postal-code" mono required />
        <CampoSelecao busca={false} rotulo="UF" valor={c.uf} aoMudar={(uf) => mudar({ uf })} vazio="UF" opcoes={UFS.map((u) => ({ valor: u, rotulo: u }))} required />
      </Grade>
      <Campo rotulo="Rua e número" valor={c.linha} aoMudar={(linha) => mudar({ linha })} autoComplete="address-line1" required />
      <Campo rotulo="Cidade" valor={c.cidade} aoMudar={(cidade) => mudar({ cidade })} autoComplete="address-level2" required />
    </>
  );
}

/** O corpo do pagamento no cartao: tokeniza e monta o que o servico espera. */
export async function corpoDoCartao(chavePublica: string | null, c: DadosDoCartao): Promise<{ ok: true; corpo: PagamentoNoCartao } | { ok: false; erro: string }> {
  if (!chavePublica) return { ok: false, erro: "O pagamento com cartão não está disponível agora." };
  const t = await tokenizar(chavePublica, c);
  if (!t.ok) return t;
  return { ok: true, corpo: { card_token: t.token, parcelas: Number(c.parcelas), endereco: { cep: c.cep, linha: c.linha, cidade: c.cidade, uf: c.uf } } };
}

function Cartao({ vitrine, enviando, aoPagar, aoErro }: { vitrine: Oferta; enviando: boolean; aoPagar: (corpo: PagamentoNoCartao) => void; aoErro: (e: string | null) => void }) {
  const [c, setC] = useState<DadosDoCartao>(CARTAO_VAZIO);
  const [tokenizando, setTokenizando] = useState(false);
  const opcao = parcelaEscolhida(c, vitrine.parcelas);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    aoErro(null);
    setTokenizando(true);
    const r = await corpoDoCartao(vitrine.chave_publica, c);
    setTokenizando(false);
    if (!r.ok) return aoErro(r.erro);
    aoPagar(r.corpo);
  };
  return (
    <form onSubmit={enviar} noValidate>
      <Pilha espaco={4}>
        <CamposDoCartao valor={c} aoMudar={setC} parcelas={vitrine.parcelas} />
        <Botao type="submit" largo icone={<IconeCartao tamanho={18} />} carregando={tokenizando || enviando} disabled={!cartaoPronto(c)}>
          Pagar {opcao ? formatarReais(opcao.total_centavos) : ""}
        </Botao>
      </Pilha>
    </form>
  );
}
